import { ReactElement, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { HiOutlinePencilSquare, HiOutlineCheck, HiOutlineXMark } from 'react-icons/hi2';
import { updateRun } from '../../../api/runs';
import { toast } from 'react-hot-toast';
import Button from '../../../components/Button';

interface RunNotesProps {
  run: any;
}

export default function RunNotes({ run }: RunNotesProps): ReactElement {
  const location = useLocation();
  const [notes, setNotes] = useState(run?.notes || '');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNotes(run?.notes || '');
  }, [run?.notes]);

  // Auto-activate edit mode if navigated from "Add Notes" menu
  useEffect(() => {
    if (location.state?.activateNotesEdit) {
      setIsEditing(true);
      // Clear the state to prevent re-activation on subsequent renders
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSave = async () => {
    if (!run?.id) {
      toast.error('Cannot save notes for this run');
      return;
    }

    setIsSaving(true);
    try {
      await updateRun(run.id, { id: run.id, notes });
      toast.success('Notes saved successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Error saving notes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNotes(run?.notes || '');
    setIsEditing(false);
  };

  return (
    <div className="cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-p-6">
      <div className="cr-mb-4 cr-flex cr-items-center cr-justify-between">
        <h3 className="cr-text-lg cr-font-semibold cr-text-gray-900">Run Notes</h3>
        {!isEditing && (
          <Button
            className="cr-text-cirrusBlue-600 hover:cr-text-cirrusBlue-800 cr-transition-colors"
            onClick={() => setIsEditing(true)}
            icon={<HiOutlinePencilSquare className="cr-size-4" />}
          />
        )}
      </div>

      {isEditing ? (
        <div className="cr-space-y-4">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="cr-focus:cr-border-cirrusBlue-500 cr-focus:cr-outline-none cr-focus:cr-ring-1 cr-focus:cr-ring-cirrusBlue-500 cr-resize-vertical cr-min-h-[120px] cr-w-full cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-2 cr-text-sm"
            placeholder="Add notes about this run..."
            rows={5}
          />
          <div className="cr-flex cr-justify-end cr-gap-2">
            <Button
              className="cr-rounded-md cr-border cr-border-gray-300 cr-px-3 cr-py-1.5 cr-text-sm cr-text-gray-700 cr-transition-colors hover:cr-bg-gray-50"
              onClick={handleCancel}
              disabled={isSaving}
              icon={<HiOutlineXMark className="cr-size-4" />}
            >
              Cancel
            </Button>
            <Button
              className="cr-bg-cirrusBlue-600 hover:cr-bg-cirrusBlue-700 cr-rounded-md cr-px-3 cr-py-1.5 cr-text-sm cr-text-white cr-transition-colors disabled:cr-opacity-50"
              onClick={handleSave}
              disabled={isSaving}
              icon={<HiOutlineCheck className="cr-size-4" />}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="cr-min-h-[60px] cr-rounded-md cr-border cr-bg-gray-50 cr-p-3">
          {notes ? (
            <p className="cr-whitespace-pre-wrap cr-text-gray-700">{notes}</p>
          ) : (
            <p className="cr-italic cr-text-gray-400">No notes added yet. Click the edit button to add notes.</p>
          )}
        </div>
      )}
    </div>
  );
}
